<template>
  <LoadingSpinner :loading='loading'> </LoadingSpinner>

  <div class="container mt-4 mb-4">
    <div style="max-width:560px">
        <h3>Kontoinnstillinger for Tripletex</h3>
        <div v-if='updated'>
          <span class='message-info'>Kontoinnstillingene er oppdatert</span>
        </div>
        <div v-if='errorMessage'>
          <span class='message-error'>{{ errorMessage }}</span>
        </div>

        <div v-if="locations.length === 0 && !loading" class="mt-4">
          <span class="message-info">Ingen avdelinger har Tripletex aktivert. Aktiver det under Avdelinger.</span>
        </div>

        <div v-if="locations.length > 0 && hasMultiLocations()" class="select-location-box mt-4">
          <span class="ingredient-title">For avdeling</span>
          <select class="form-select" v-model="locationId" @change="getAccountSettings()">
            <option v-for="(location, index) in locations" :key="index" :value="location.id">{{ location.name }}</option>
          </select>
        </div>

        <template v-if="locations.length > 0">
          <!-- Locked when this location's Tripletex connection is live. These are not cosmetic settings:
               a department, an account number and the auto-sync toggle decide what gets posted to a real
               ledger and where it lands. The environment lives on the account document, so it is fetched
               alongside — see loadEnvironment(). -->
          <config-lock ref="lock" :configured="accountIsProduction">
          <div class="mt-4">
            <span class='ingredient-title'>Tips konto</span>
            <input class='form-control' type='number' v-model.number='accountSettings.tipsAccountNumber' />
          </div>

          <div class="mt-3">
            <span class='ingredient-title'>Bonus konto</span>
            <input class='form-control' type='number' v-model.number='accountSettings.bonusAccountNumber' />
          </div>

          <div class="mt-3">
            <span class='ingredient-title'>Avdelings-ID (department)</span>
            <input class='form-control' type='number' v-model.number='accountSettings.departmentId' />
          </div>

          <div class="form-switch-wrapper mt-4">
            <label class="form-switch">
              <div class="form-switch-container">
                <input type="checkbox" v-model="accountSettings.autoSyncEnabled" />
                <i></i>
                <span class="bold ms-2">Automatisk synkronisering</span>
              </div>
            </label>
          </div>

          <div class='bo_hr mt-4'>
            <hr/>
          </div>

          <div class='mb-5'>
            <button type='button' class='bo_btn' @click='save'> Lagre </button>
          </div>
          </config-lock>
        </template>
    </div>
  </div>
</template>

<script>
  import SettingsService from '@/services/settings/SettingsService';
  import MerchantUtil from "@/util/MerchantUtil";
  import LoadingSpinner from "@/components/other/LoadingSpinner";
  import LocationUtil from "@/util/LocationUtil";
  import LocationService from "@/services/location/LocationService";
  import ConfigLock from "@/components/other/ConfigLock.vue";
  import AccountingEnv from "@/util/AccountingEnv";

  export default {
    components: {LoadingSpinner, ConfigLock},
    data() {
      return {
        loading: true,
        locationId: '',
        locations: [],
        // Starts true so the form is locked before we know: the environment arrives asynchronously, and
        // an unlocked moment on a production config is the failure this whole guard is against.
        accountIsProduction: true,
        updated: false,
        errorMessage: '',
        accountSettings: {
          type: "tripletex_account_settings",
          merchant: '',
          locationId: '',
          tipsAccountNumber: 3902,
          bonusAccountNumber: 3903,
          departmentId: 0,
          autoSyncEnabled: false
        }
      };
    },
    mounted() {
      LocationService.listLocations().then(
          (response) => {
            this.locations = response.data.filter(l => l.accountingSystem === 'TRIPLETEX');
            this.locationId = this.locations.length ? this.locations[0].id : '';

            if (this.locationId) {
              this.getAccountSettings();
            } else {
              this.loading = false;
            }
          },
          (error) => {
            console.log(error);
          }
      );
    },
    methods: {
      hasMultiLocations(){
        return LocationUtil.hasMultiLocations();
      },
      /**
       * Which environment this location's connection points at.
       *
       * It lives on the ACCOUNT document, not on these settings, so it has to be fetched separately.
       * Fails safe in both directions: an error leaves accountIsProduction true, so an unreachable
       * backend locks the form rather than opening it.
       */
      loadEnvironment(){
        SettingsService.getTripletexAccount(this.locationId).then(
            (response) => {
              const acc = response.data || {};
              this.accountIsProduction = AccountingEnv.isProduction(acc.baseApiUrl);
              this.$nextTick(() => this.$refs.lock && this.$refs.lock.reset());
            },
            (error) => {
              console.log(error);
              this.accountIsProduction = true;
              this.$nextTick(() => this.$refs.lock && this.$refs.lock.reset());
            }
        );
      },
      getAccountSettings(){
        this.loading = true;
        // One entry point, so mounted() and the location selector cannot get different answers.
        this.loadEnvironment();
        this.updated = false;
        this.errorMessage = '';
        SettingsService.getTripletexAccountSettings(this.locationId).then(
            (response) => {
              if(response.data !== null){
                this.accountSettings = response.data;
              }
              this.loading = false;
            },
            (error) => {
              console.log(error);
              this.loading = false;
            }
        );
      },
      save(){
        this.loading = true;
        this.updated = false;
        this.errorMessage = '';
        this.accountSettings.merchant = MerchantUtil.getMerchant();
        this.accountSettings.locationId = this.locationId;

        SettingsService.editTripletexAccountSettings(this.accountSettings).then(
            (response) => {
              let documentId = response.data;
              this.accountSettings.id = documentId;
              if(documentId){
                this.updated = true;
              } else {
                this.errorMessage = 'Lagring feilet';
              }
              this.loading = false;
              // Back to the lock the saved config calls for: production re-locks, test stays open.
              this.$nextTick(() => this.$refs.lock && this.$refs.lock.reset());
            },
            (error) => {
              console.log(error);
              this.errorMessage = 'Lagring feilet';
              this.loading = false;
            }
        );
      }
    }
  };
</script>
